// Political borders as a terrain surface layer: every fragment knows its hex's owner and its six
// neighbours' owners (hexdata owner texture), so borders drape on any LOD with no mesh to rebuild.
//
//        neighbour owned by B          ┌ edge line: owner's secondary colour, crisp
//   ───────────────────────────────────┤ ribbon:    owner's primary colour, emissive (bloom)
//     ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓    └ gradient:  primary fading inward over 0.45 R
//     ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░      fill:      faint primary tint that grows with zoom-out
//              owned by A
//
// Each side of a border draws its own ribbon inside its own hexes (two ribbons side by side, like
// Civ). Widths are in world units with a pixel floor, so a border stays a crisp line from the
// close view to the whole world. Coasts (neighbour unowned) get a lighter line.
//
// Occupation: where a controller differs from the owner, diagonal stripes in the controller's
// colour cross the owner's fill (ARCHITECTURE §6), as strong as the controlled share of the hex.
// Front: a burning line on edges where control changes hands inside a country (the controlled
// side holds a majority of its hex). Both come from the snapshot's sourced control data.
// At war with the viewing leader, the ribbon pulses slowly (hexdata RELATION_CODE): brightest on
// the border the two states share, softer along the war state's other borders.
//
// Claims (snapshot.claim): where the leader's point of view gives a hex to a state other than the
// default worldview, the hex is drawn in the view's owner colour with a diagonal hatch (the
// other way round from occupation stripes) and a dashed outline in the recognised owner's colour:
//
//     ┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄  dashes: recognised owner's colour, on the edge of the claimed area
//     ╲╲╲╲╲╲╲╲╲╲╲╲╲╲╲╲  hatch:  view owner's colour, lightened
//
// Leader lens (setLens 'political'): the ribbon takes the diplomatic relation colour instead of
// the nation colour (ARCHITECTURE §7.2).

import { float, max, min, mix, select, sin, smoothstep, vec2, vec3 } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { HILL, TERRAIN } from '../../core/schema.ts';
import { RELATION_CODE } from '../hexdata.ts';
import type { SurfaceCtx, SurfaceLayer } from '../surface.ts';
import { rgb } from '../tsl/color.ts';
import { edgeSegmentDistance, neighbourCell } from '../tsl/hex.ts';
import { worldNoise } from '../tsl/noise.ts';
import type { ClaimField } from './claims.ts';

type F = Node<'float'>;
type V3 = Node<'vec3'>;

// Widths (world units) and their pixel floors.
const RIBBON = 0.085;
const RIBBON_MIN_PX = 1.6;
const EDGE = 0.022;
const EDGE_MIN_PX = 0.7;
const EDGE_SECONDARY = 0.7; // edge line: mostly the secondary colour, tied to the primary
const GRADIENT_REACH = 0.45;
const GRADIENT = 0.3;
const COAST_STRENGTH = 0.55;

// Territory fill grows as the camera rises (hex radii): the world view reads as a political map.
const FILL = [0.03, 0.13] as const;
const FILL_HEIGHT = [30, 400] as const;

const RIBBON_GLOW = 0.28;
const EDGE_GLOW = 0.18;
const WAR_PULSE_HZ = 0.6;
const WAR_PULSE = 0.35;
const WAR_PULSE_SHARED = 1; // border shared with the leader's state
const WAR_PULSE_OTHER = 0.45; // the war state's other borders

// Occupation hatching (stripes per world unit, share of the period that is stripe).
const HATCH_FREQ = 5;
const HATCH_DUTY = 0.38;
const HATCH_STRENGTH = 0.55;

// Claims: extra fill, hatch stripes (per world unit, share that is stripe) and the dashed outline.
const CLAIM_FILL = 0.3;
const CLAIM_HATCH_FREQ = 5;
const CLAIM_HATCH_DUTY = 0.42;
const CLAIM_HATCH_STRENGTH = 0.5;
const CLAIM_HATCH_ROCK = 0.35; // strength cap on mountains, where the stripes bend over the relief
const CLAIM_HATCH_LIGHTEN = 0.3;
// Stripe period in pixels: stripes drawn from [1] up, gone below [0]. Under [1] the pattern hands
// over to stripes twice as wide, so the hatch keeps a calm rhythm from close up to far out.
const CLAIM_PERIOD_PX = [6, 10] as const;
const CLAIM_SLOPE = [0.05, 0.35] as const; // 1 - normal.y: stripes full on flat ground, gone on steep
const DASH_FREQ = 4; // dashes per world unit
const DASH_DUTY = 0.6;
const DASH_WIDTH = 0.05;
const DASH_MIN_PX = 1.4;
const DASH_INSET = 0.75; // dash centre, in dash widths from the edge
const DASH_CASING = 1.7; // casing width, in dash widths
const DASH_SOLID_PX = [2, 6] as const; // dash period in pixels: solid below the first, dashed above the second
const DASH_GLOW = 0.25;

// Leader lens: ribbon colour per relation code (mirrors the political lens legend, world/lenses.ts).
const RELATION_COLORS: ReadonlyArray<readonly [number, string]> = [
  [RELATION_CODE.self, '#f2c14e'],
  [RELATION_CODE.war, '#c0392b'],
  [RELATION_CODE.ally, '#2e86de'],
  [RELATION_CODE.friend, '#27ae60'],
  [RELATION_CODE.neutral, '#95a5a6'],
  [RELATION_CODE.denounced, '#8e44ad'],
];
const RELATION_RIM = 0.55; // edge line: relation colour scaled by this

// Front line: width, colour, flicker.
const FRONT = 0.07;
const FRONT_MIN_PX = 2;
const FRONT_COLOR = '#ff6a2b';
const FRONT_CORE = '#ffd27a';
const FRONT_GLOW = 1.1;
const FRONT_FLICKER_FREQ = 3.5;
const MAJORITY = 128; // controlFrac (0..255) at which a hex counts as held by its controller

export interface BorderDeps {
  /** Claim texture (snapshot.claim); without it nothing is hatched. */
  claims?: ClaimField;
  /** 1 under the leader lens (relation colours), 0 otherwise. */
  relationLens?: F;
  /** 0..1 coverage by a front-line ribbon; the hex-edge front fades out under one. */
  frontCover?: (ctx: SurfaceCtx) => F;
}

interface BorderMask {
  /** Distance to the nearest front edge → 0..1 line strength. */
  front: F;
  /** Controlled share of this hex, 0..1. */
  frac: F;
  own: F;
  has: F;
  /** Distance to the nearest edge whose neighbour has a different owner (9 = none). */
  dist: F;
  /** 1 when that nearest border edge is a coast (neighbour unowned). */
  coast: F;
  ribbon: F;
  edge: F;
  controller: F;
  primary: V3;
  secondary: V3;
  /** Recognised owner of a claimed hex (0 = not claimed). */
  claim: F;
  /** Distance to the nearest edge of the claimed area (9 = none) and the dash phase along it. */
  dash: F;
  dashPhase: F;
  /** 0..1 war pulse on this fragment's border. */
  pulse: F;
}

/** Who holds a hex: its controller when that controls a majority of it, else its owner. */
function holder(ctx: SurfaceCtx, cell: ReturnType<typeof neighbourCell>, owner: F): { who: F; occupied: F } {
  const ctl = ctx.data.controllerAt(cell);
  const frac = ctx.data.stateAt(cell).y;
  const held = ctl.greaterThan(0.5).and(ctl.notEqual(owner)).and(frac.greaterThanEqual(MAJORITY));
  return { who: select(held, ctl, owner), occupied: select(held, float(1), float(0)) };
}

/** Relation colour of a RELATION_CODE node; `fallback` when the code is unknown (0). */
function relationColor(rel: F, fallback: V3): V3 {
  let out = fallback;
  for (const [code, css] of RELATION_COLORS) {
    out = select(rel.equal(code), rgb(css), out);
  }
  return out;
}

function borderMask(ctx: SurfaceCtx, deps: BorderDeps): BorderMask {
  const { data, hex } = ctx;
  const own = data.ownerAt(hex).toVar('borderOwn');
  const dist = float(9).toVar('borderDist');
  const coast = float(0).toVar('borderCoast');
  const frontDist = float(9).toVar('frontDist');
  const nearOwner = float(0).toVar('borderNear');
  const claim = deps.claims ? deps.claims.at(hex).toVar('claimOwn') : float(0);
  const dash = float(9).toVar('claimDash');
  const dashPhase = float(0).toVar('claimPhase');
  const self = holder(ctx, hex, own);
  for (let d = 0; d < 6; d++) {
    const cell = neighbourCell(hex, d);
    const nb = data.ownerAt(cell);
    const other = holder(ctx, cell, nb);
    const front = nb.equal(own).and(other.who.notEqual(self.who)).and(self.occupied.add(other.occupied).greaterThan(0.5));
    frontDist.assign(min(frontDist, select(front, edgeSegmentDistance(hex.local, d), float(9))));
    const differs = nb.notEqual(own);
    const e = select(differs, edgeSegmentDistance(hex.local, d), float(9));
    const closer = e.lessThan(dist);
    coast.assign(select(closer, select(nb.lessThan(0.5), float(1), float(0)), coast));
    nearOwner.assign(select(closer, nb, nearOwner));
    dist.assign(min(dist, e));

    // Claimed area outline: edges where the neighbour's claim differs from this hex's claim.
    if (deps.claims) {
      const edgeOfClaim = claim.greaterThan(0.5).and(deps.claims.at(cell).notEqual(claim));
      const de = select(edgeOfClaim, edgeSegmentDistance(hex.local, d), float(9));
      const angle = (Math.PI / 3) * d;
      const along = ctx.xz.x.mul(-Math.sin(angle)).add(ctx.xz.y.mul(Math.cos(angle)));
      dashPhase.assign(select(de.lessThan(dash), along, dashPhase));
      dash.assign(min(dash, de));
    }
  }
  // Land only: the sea floor under the water plane never shows territory.
  const land = data.terrainAt(hex).terrain.greaterThan(TERRAIN.COAST + 0.5);
  const has = select(own.greaterThan(0.5).and(land), float(1), float(0));
  const aa = ctx.px.mul(0.75);
  const ribbonW = max(float(RIBBON), ctx.px.mul(RIBBON_MIN_PX));
  const edgeW = max(float(EDGE), ctx.px.mul(EDGE_MIN_PX));
  const strength = mix(float(1), float(COAST_STRENGTH), coast).mul(has);
  const ribbon = smoothstep(ribbonW.add(aa), ribbonW.sub(aa), dist).mul(strength);
  // No bright edge on coasts: it would double the surf line and read like a hex ring.
  const edge = smoothstep(edgeW.add(aa), edgeW.sub(aa), dist).mul(has).mul(coast.oneMinus());
  const controller = data.controllerAt(hex);
  const frontW = max(float(FRONT), ctx.px.mul(FRONT_MIN_PX));
  let front = smoothstep(frontW.add(aa), frontW.sub(aa), frontDist).mul(has);
  if (deps.frontCover) {
    // A front ribbon (fronts.ts) replaces the hex-edge front wherever a polyline runs.
    front = front.mul(deps.frontCover(ctx).oneMinus());
  }
  const frac = data.stateAt(hex).y.div(255);

  // Nation colours, or relation colours under the leader lens.
  const nationPrimary = data.actorColor(own, 0);
  const nationSecondary = data.actorColor(own, 1);
  const lens = deps.relationLens ?? float(0);
  const relColor = relationColor(data.actorRelation(own), nationPrimary);
  const primary = mix(nationPrimary, relColor, lens).toVar('borderPrimary');
  const secondary = mix(nationSecondary, relColor.mul(RELATION_RIM), lens).toVar('borderSecondary');

  return {
    front, frac, own, has, dist, coast, ribbon, edge, controller, primary, secondary, claim, dash, dashPhase,
    pulse: warPulse(ctx, own, nearOwner),
  };
}

/** Flicker of the front line (0.6..1), travelling along it both ways. */
function flicker(ctx: SurfaceCtx): F {
  const a = worldNoise(ctx.xz.add(vec2(ctx.time.mul(0.7), 0)), FRONT_FLICKER_FREQ);
  const b = worldNoise(ctx.xz.sub(vec2(ctx.time.mul(0.5), ctx.time.mul(0.3))), FRONT_FLICKER_FREQ * 1.7);
  return a.mul(0.5).add(b.mul(0.5)).mul(0.4).add(0.6);
}

/**
 * Slow pulse (0..1 extra) on borders of a state at war with the viewing leader: full strength on
 * the border it shares with the leader's state (both sides), softer along its other borders.
 *
 *   own at war, neighbour = leader's state  → WAR_PULSE_SHARED
 *   own = leader's state, neighbour at war  → WAR_PULSE_SHARED
 *   own at war, neighbour anyone else       → WAR_PULSE_OTHER
 */
function warPulse(ctx: SurfaceCtx, own: F, nearOwner: F): F {
  const ownRel = ctx.data.actorRelation(own);
  const nearRel = ctx.data.actorRelation(nearOwner);
  const ownWar = ownRel.equal(RELATION_CODE.war);
  const nearSelf = nearRel.equal(RELATION_CODE.self);
  const shared = ownWar.and(nearSelf).or(ownRel.equal(RELATION_CODE.self).and(nearRel.equal(RELATION_CODE.war)));
  const weight = select(shared, float(WAR_PULSE_SHARED), select(ownWar, float(WAR_PULSE_OTHER), float(0)));
  return sin(ctx.time.mul(Math.PI * 2 * WAR_PULSE_HZ)).mul(0.5).add(0.5).mul(weight).mul(WAR_PULSE);
}

/** Claim stripes at `freq` per world unit, and the weight the size of their period on screen earns them. */
function claimStripes(ctx: SurfaceCtx, freq: number): { pattern: F; weight: F } {
  const stripe = ctx.xz.x.sub(ctx.xz.y).mul(freq).fract();
  const periodPx = float(1 / freq).div(ctx.px.max(1e-5));
  return {
    pattern: smoothstep(CLAIM_HATCH_DUTY + 0.04, CLAIM_HATCH_DUTY, stripe),
    weight: smoothstep(CLAIM_PERIOD_PX[0], CLAIM_PERIOD_PX[1], periodPx),
  };
}

/**
 * Claim hatch stripes (0..1). The period widens as the camera pulls back (the stripes of twice the
 * width take over as the fine ones get too thin to read), and the stripes fade out over slopes,
 * where they would bend into a zigzag: the relief is worth more than the hatch there.
 */
function claimHatch(ctx: SurfaceCtx): F {
  const fine = claimStripes(ctx, CLAIM_HATCH_FREQ);
  const wide = claimStripes(ctx, CLAIM_HATCH_FREQ / 2);
  const stripes = fine.pattern.mul(fine.weight).add(wide.pattern.mul(wide.weight).mul(fine.weight.oneMinus()));
  return stripes.mul(smoothstep(CLAIM_SLOPE[1], CLAIM_SLOPE[0], ctx.normal.y.oneMinus()));
}

interface Dashes {
  /** Dash body (recognised owner's primary). */
  core: F;
  /** Slightly wider casing (its secondary) that keeps the dashes legible on similar colours. */
  casing: F;
}

/** Dashed outline of the claimed area; the dashes merge into a solid line when zoomed out. */
function claimDashes(ctx: SurfaceCtx, m: BorderMask): Dashes {
  const width = max(float(DASH_WIDTH), ctx.px.mul(DASH_MIN_PX));
  const aa = ctx.px.mul(0.75);
  const centre = m.dash.sub(width.mul(DASH_INSET)).abs().mul(2);
  const core = smoothstep(width.add(aa), width.sub(aa), centre);
  const casing = smoothstep(width.mul(DASH_CASING).add(aa), width.mul(DASH_CASING).sub(aa), centre);

  // Dash period in pixels: solid (duty-weighted) below DASH_SOLID_PX[0], full pattern above [1].
  const periodPx = float(1 / DASH_FREQ).div(ctx.px.max(1e-5));
  const pattern = smoothstep(DASH_DUTY + 0.03, DASH_DUTY - 0.03, m.dashPhase.mul(DASH_FREQ).fract());
  const dashed = mix(float(DASH_DUTY), pattern, smoothstep(DASH_SOLID_PX[0], DASH_SOLID_PX[1], periodPx));
  const mask = dashed.mul(m.has).mul(select(m.claim.greaterThan(0.5), float(1), float(0)));
  return { core: core.mul(mask), casing: casing.mul(mask) };
}

export function borderLayer(deps: BorderDeps = {}): SurfaceLayer {
  const cache = new WeakMap<object, BorderMask>();
  const get = (ctx: SurfaceCtx): BorderMask => {
    const hit = cache.get(ctx.hex);
    if (hit) {
      return hit;
    }
    const m = borderMask(ctx, deps);
    cache.set(ctx.hex, m);
    return m;
  };
  return {
    name: 'borders',
    order: 300,
    color(ctx) {
      const m = get(ctx);
      const { primary, secondary } = m;
      const claimed = select(m.claim.greaterThan(0.5), float(1), float(0));
      const fill = mix(float(FILL[0]), float(FILL[1]), smoothstep(FILL_HEIGHT[0], FILL_HEIGHT[1], ctx.camHeight));
      const gradient = smoothstep(GRADIENT_REACH, 0, m.dist).mul(GRADIENT).mul(m.coast.oneMinus().mul(0.5).add(0.5));
      let c: V3 = mix(ctx.color, primary, fill.add(gradient).max(claimed.mul(CLAIM_FILL)).min(1).mul(m.has));

      // Occupation: stripes in the controller's colour.
      const occupied = select(m.controller.greaterThan(0.5).and(m.controller.notEqual(m.own)), float(1), float(0));
      const stripe = ctx.xz.x.add(ctx.xz.y).mul(HATCH_FREQ).fract();
      const hatch = smoothstep(HATCH_DUTY + 0.04, HATCH_DUTY, stripe).mul(smoothstep(0.08, 0.02, ctx.px));
      c = mix(c, ctx.data.actorColor(m.controller, 0), hatch.mul(occupied).mul(m.frac).mul(m.has).mul(HATCH_STRENGTH));

      // Claim: the other diagonal, in the view owner's colour lightened.
      if (deps.claims) {
        const claimInk = mix(primary, vec3(1), CLAIM_HATCH_LIGHTEN);
        const rock = ctx.data.terrainAt(ctx.hex).hill.equal(HILL.MOUNTAINS);
        const strength = select(rock, float(CLAIM_HATCH_ROCK), float(CLAIM_HATCH_STRENGTH));
        c = mix(c, claimInk, claimHatch(ctx).mul(claimed).mul(m.has).mul(strength));
      }

      const lit = primary.mul(float(1).add(m.pulse));
      c = mix(c, lit, m.ribbon);
      c = mix(c, mix(primary, secondary, EDGE_SECONDARY), m.edge);
      if (deps.claims) {
        const dashes = claimDashes(ctx, m);
        c = mix(c, ctx.data.actorColor(m.claim, 1), dashes.casing);
        c = mix(c, ctx.data.actorColor(m.claim, 0), dashes.core);
      }
      return mix(c, mix(rgb(FRONT_COLOR), rgb(FRONT_CORE), smoothstep(0.5, 1, m.front)), m.front);
    },
    emissive(ctx) {
      const m = get(ctx);
      const pulse = float(1).add(m.pulse.mul(3));
      const front = rgb(FRONT_COLOR).mul(m.front.mul(FRONT_GLOW).mul(flicker(ctx)));
      let glow: V3 = m.primary.mul(m.ribbon.mul(RIBBON_GLOW).mul(pulse)).add(m.secondary.mul(m.edge.mul(EDGE_GLOW))).add(front);
      if (deps.claims) {
        glow = glow.add(ctx.data.actorColor(m.claim, 0).mul(claimDashes(ctx, m).core.mul(DASH_GLOW)));
      }
      return glow;
    },
  };
}
